import Phaser from 'phaser';
import { floatingBarState, enemyBarColor } from '@/systems/EnemyHealthBar';
import type { Enemy } from '@/entities/Enemy';
import type { BossEnemy } from '@/entities/BossEnemy';

const ENEMY_BAR_W = 44;
const ENEMY_BAR_H = 5;
const BOSS_BAR_W = 110;
const BOSS_BAR_H = 8;
/** Pixels above the entity's top edge. */
const BAR_ABOVE = 8;
/** Depth above DepthSort maximum (groundY ≤ canvas height ≈ 720). */
const BAR_DEPTH = 800;

interface BarEntry {
  bg: Phaser.GameObjects.Rectangle;
  fg: Phaser.GameObjects.Rectangle;
}

interface BossBarEntry extends BarEntry {
  label: Phaser.GameObjects.Text;
}

/**
 * EnemyHealthBarPool — Phaser adapter for floating enemy HP bars.
 *
 * GameObjects are created once and reused across frames (no per-frame alloc).
 * All bars live in world space inside StageScene so the camera carries them
 * without any manual scroll-offset arithmetic.
 */
export class EnemyHealthBarPool {
  private readonly scene: Phaser.Scene;

  private readonly pool = new Map<number, BarEntry>();

  private bossBar?: BossBarEntry;

  /** combatIds that have received at least one point of damage this run. */
  private readonly damagedIds = new Set<number>();

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Call once per frame after entity positions are finalised.
   * Updates bar positions, sizes, and visibility.
   */
  update(enemies: readonly Enemy[], boss?: BossEnemy, bossLabel = ''): void {
    const activeIds = new Set<number>();

    for (const enemy of enemies) {
      const id = enemy.combatId;
      activeIds.add(id);

      if (enemy.health.fraction < 1) this.damagedIds.add(id);
      const everDamaged = this.damagedIds.has(id);
      const state = floatingBarState(enemy.health.fraction, enemy.isKO, everDamaged);

      const entry = this.getOrCreateEntry(id, ENEMY_BAR_W, ENEMY_BAR_H);
      entry.bg.setVisible(state.visible);
      entry.fg.setVisible(state.visible);

      if (state.visible) {
        const topY = enemy.y - enemy.entityHeight / 2 - BAR_ABOVE - ENEMY_BAR_H;
        const leftX = enemy.x - ENEMY_BAR_W / 2;
        entry.bg.setPosition(leftX, topY);
        entry.fg.setPosition(leftX, topY);
        entry.fg.setSize(Math.max(0, Math.round(state.hpFrac * ENEMY_BAR_W)), ENEMY_BAR_H);
        entry.fg.setFillStyle(state.color);
      }
    }

    // Hide bars for enemies removed from the active list (KO'd + culled).
    for (const [id, entry] of this.pool) {
      if (!activeIds.has(id)) {
        entry.bg.setVisible(false);
        entry.fg.setVisible(false);
      }
    }

    this.updateBossBar(boss, bossLabel);
  }

  /** Hide all bars and forget damage state (call on respawn / wave restart). */
  reset(): void {
    for (const entry of this.pool.values()) {
      entry.bg.setVisible(false);
      entry.fg.setVisible(false);
    }
    if (this.bossBar) {
      this.bossBar.bg.setVisible(false);
      this.bossBar.fg.setVisible(false);
      this.bossBar.label.setVisible(false);
    }
    this.damagedIds.clear();
  }

  // ── private ────────────────────────────────────────────────────────────────

  private getOrCreateEntry(id: number, barW: number, barH: number): BarEntry {
    let entry = this.pool.get(id);
    if (!entry) {
      const bg = this.scene.add
        .rectangle(0, 0, barW, barH, 0x000000, 0.7)
        .setOrigin(0, 0)
        .setDepth(BAR_DEPTH);
      const fg = this.scene.add
        .rectangle(0, 0, barW, barH, 0xffffff)
        .setOrigin(0, 0)
        .setDepth(BAR_DEPTH + 1);
      entry = { bg, fg };
      this.pool.set(id, entry);
    }
    return entry;
  }

  private updateBossBar(boss: BossEnemy | undefined, label: string): void {
    const visible = !!boss && !boss.isKO && boss.hpFraction > 0;

    if (!this.bossBar) {
      const bg = this.scene.add
        .rectangle(0, 0, BOSS_BAR_W, BOSS_BAR_H, 0x000000, 0.8)
        .setOrigin(0, 0)
        .setDepth(BAR_DEPTH);
      const fg = this.scene.add
        .rectangle(0, 0, BOSS_BAR_W, BOSS_BAR_H, 0xff4444)
        .setOrigin(0, 0)
        .setDepth(BAR_DEPTH + 1);
      const lbl = this.scene.add
        .text(0, 0, '', {
          fontFamily: '"Courier New", monospace',
          fontSize: '9px',
          color: '#ef233c',
          stroke: '#000000',
          strokeThickness: 2,
        })
        .setOrigin(0.5, 1)
        .setDepth(BAR_DEPTH + 1);
      this.bossBar = { bg, fg, label: lbl };
    }

    this.bossBar.bg.setVisible(visible);
    this.bossBar.fg.setVisible(visible);
    this.bossBar.label.setVisible(visible);

    if (visible && boss) {
      const topY = boss.y - boss.entityHeight / 2 - BAR_ABOVE - BOSS_BAR_H;
      const leftX = boss.x - BOSS_BAR_W / 2;
      const frac = boss.hpFraction;

      this.bossBar.bg.setPosition(leftX, topY);
      this.bossBar.bg.setSize(BOSS_BAR_W, BOSS_BAR_H);
      this.bossBar.fg.setPosition(leftX, topY);
      this.bossBar.fg.setSize(Math.max(0, Math.round(frac * BOSS_BAR_W)), BOSS_BAR_H);
      this.bossBar.fg.setFillStyle(enemyBarColor(frac));
      this.bossBar.label.setPosition(boss.x, topY);
      this.bossBar.label.setText(label.toUpperCase());
    }
  }
}
